import dayjs from 'dayjs';

export interface QuarterOption {
  year: number;          // Calendar year (actual quarter period)
  quarter: number;       // Calendar quarter (actual quarter period)
  label: string;         // Display label (e.g., "Q1 2025")
  value: string;         // Value for selection (e.g., "2025-1")
  isCurrentQuarter: boolean;
}

/**
 * Get the month name for a quarter's end month
 */
export const getQuarterEndMonth = (quarter: number): string => {
  const quarterMonths = {
    1: 'March',
    2: 'June', 
    3: 'September',
    4: 'December'
  };
  
  return quarterMonths[quarter as keyof typeof quarterMonths] || 'Unknown';
};

/**
 * Get the current quarter based on today's date
 * We're currently in Q2 2025
 */
export const getCurrentQuarter = (): { year: number; quarter: number } => {
  const now = dayjs();
  const month = now.month() + 1; // dayjs months are 0-indexed
  
  let quarter: number;
  if (month <= 3) quarter = 1;
  else if (month <= 6) quarter = 2;
  else if (month <= 9) quarter = 3;
  else quarter = 4;
  
  return {
    year: now.year(),
    quarter,
  };
};

/**
 * Check if a quarter is in the past (earnings would be available)
 */
export const isQuarterInPast = (year: number, quarter: number): boolean => {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month() + 1;
  
  // Determine current quarter
  let currentQuarter: number;
  if (currentMonth <= 3) currentQuarter = 1;
  else if (currentMonth <= 6) currentQuarter = 2;
  else if (currentMonth <= 9) currentQuarter = 3;
  else currentQuarter = 4;
  
  // Compare year first, then quarter
  if (year < currentYear) return true;
  if (year > currentYear) return false;
  
  // Same year, compare quarters
  return quarter < currentQuarter;
};

/**
 * Generate quarter options going back in time from the most recent completed quarter
 * Only shows quarters where earnings would be available (past quarters)
 */
export const generateQuarterOptions = (yearsBack: number = 5): QuarterOption[] => {
  const { year: currentYear, quarter: currentQuarter } = getCurrentQuarter();
  const options: QuarterOption[] = [];
  
  let year = currentYear;
  let quarter = currentQuarter;
  
  // Start from the current quarter (FMP already provides correct quarter labels)
  
  // Generate quarters going back in time (only past quarters)
  for (let i = 0; i < yearsBack * 4; i++) {
    // Only include quarters that are in the past
    if (isQuarterInPast(year, quarter) || (year === currentYear && quarter === currentQuarter)) {
      const isCurrentQuarter = year === currentYear && quarter === currentQuarter;
      
      options.push({
        year,
        quarter,
        label: `Q${quarter} ${year}`,
        value: `${year}-${quarter}`,
        isCurrentQuarter,
      });
    }
    
    // Move to previous quarter
    quarter--;
    if (quarter === 0) {
      quarter = 4;
      year--;
    }
  }
  
  return options;
};

/**
 * Format earnings date for display
 */
export const formatEarningsDate = (date: string): string => {
  return dayjs(date).format('MMMM D, YYYY');
};

/**
 * Get quarter end date for a given year and quarter
 */
export const getQuarterEndDate = (year: number, quarter: number): string => {
  const quarterEndMonths = { 1: '03', 2: '06', 3: '09', 4: '12' };
  const quarterEndDays = { 1: '31', 2: '30', 3: '30', 4: '31' };
  
  return `${year}-${quarterEndMonths[quarter as keyof typeof quarterEndMonths]}-${quarterEndDays[quarter as keyof typeof quarterEndDays]}`;
};

/**
 * Parse quarter value string back to calendar year and quarter
 */
export const parseQuarterValue = (value: string): { year: number; quarter: number } => {
  const [yearStr, quarterStr] = value.split('-');
  return {
    year: parseInt(yearStr, 10),    // Calendar year
    quarter: parseInt(quarterStr, 10), // Calendar quarter
  };
};

/**
 * Paginate quarter options (2 years = 8 quarters per page)
 */
export const paginateQuarters = (quarters: QuarterOption[], page: number, quartersPerPage: number = 8): {
  quarters: QuarterOption[];
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
} => {
  const totalPages = Math.ceil(quarters.length / quartersPerPage);
  const startIndex = page * quartersPerPage;
  const endIndex = startIndex + quartersPerPage;
  
  return {
    quarters: quarters.slice(startIndex, endIndex),
    totalPages,
    hasNext: page < totalPages - 1,
    hasPrevious: page > 0,
  };
};